import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  DragIndicator,
  Save,
  Close,
  CheckCircle,
  WatchLater,
} from "@mui/icons-material";
import {
  getTenantMappings,
  saveTenantMappings,
} from "./DataServices/ReminderService";
import {
  fetchVenmoCandidates,
  saveVenmoMemberMappings,
  fetchGroupOccupantsForArh,
} from "./DataServices/ProspectiveTenantDataService";
import {
  IPublicTenantLead,
  IAutomaticResponseHistory,
  AutoResponseDetailsinfo,
  MappingItem,
  MemberGuarantorInfo,
  IVenmoCandidate,
  IConfirmedAppfolioAssignment,
} from "./Models";
import MemberStatusFlow from "./MemberStatusFlow";
import Loading from "./Loading";

interface MappedAppfolioMember extends IPublicTenantLead {
  role: "tenant" | "guarantor";
  displayName: string;
  dragId: string;
  // "publicLead" = legacy flat PublicTenantLead (Source 1, default).
  // "groupOccupant" = group-aware AppFolio scrape (Source 2), projected into the same
  // shape (apartmentTitle -> apartmentName, isCosigner -> isGuarantor, phone -> phoneNo).
  source: "publicLead" | "groupOccupant";
  // Live-computed suggestion from the backend (not a saved mapping) — mirrors
  // IVenmoCandidate.matchedMemberId. Only ever set on "groupOccupant" entries.
  matchedMemberId?: string | null;
}

interface MappedVenmoCandidate {
  dragId: string;
  displayName: string;
  key: string;
  amount: number;
  paymentDate?: string | null;
  transactionId?: string | null;
  isExplicitMatch?: boolean;
}

interface FormMemberSlot {
  formMember: MemberGuarantorInfo;
  formMemberIndex: number;
  matchedLead: MappedAppfolioMember | MappedVenmoCandidate | null;
}

type DrawerTab = "progress" | "appfolio" | "venmo";

interface GroupDetailsDrawerProps {
  arh: IAutomaticResponseHistory;
  onClose: () => void;
  appfolioData: IPublicTenantLead[];
  memberGuarantorInfo?: AutoResponseDetailsinfo;
  mappingInfo?: {
    totalMembers: number;
    mappingTotalMembers: number;
    mappedMembers: number;
  };
  isMandatory: boolean;
  onMappingsUpdated?: () => Promise<void> | void;
  getExternalFormStatus: () => { status: string; color: string };
  getAppfolioStatus: () => { status: string; color: string };
  editState?: {
    tenants: number;
    guarantors: number;
    payment: string;
    bidding: string;
    changed: boolean;
  };
  onEditChange: (
    field: "tenants" | "guarantors" | "payment" | "bidding",
    value: number | string,
  ) => void;
  onSave: () => void;
  onOpenInUnsignedList?: () => void;
  confirmedAppfolioAssignments?: IConfirmedAppfolioAssignment[];
}

const GroupDetailsDrawer: React.FC<GroupDetailsDrawerProps> = ({
  arh,
  onClose,
  appfolioData,
  memberGuarantorInfo,
  mappingInfo,
  isMandatory,
  onMappingsUpdated,
  getExternalFormStatus,
  getAppfolioStatus,
  editState,
  onEditChange,
  onSave,
  onOpenInUnsignedList,
  confirmedAppfolioAssignments,
}) => {
  const [activeTab, setActiveTab] = useState<DrawerTab>("progress");

  const [existingMappings, setExistingMappings] = useState<MappingItem[]>([]);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"success" | "error" | null>(
    null,
  );
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(
    null,
  );

  const [slotMappings, setSlotMappings] = useState<{
    [formMemberIndex: string]: MappedAppfolioMember | null;
  }>({});
  const [draggedLead, setDraggedLead] = useState<MappedAppfolioMember | null>(
    null,
  );
  const [dragOverSlot, setDragOverSlot] = useState<string | null>(null);

  const [venmoCandidatesList, setVenmoCandidatesList] = useState<
    {
      dragId: string;
      displayName: string;
      key: string;
      amount: number;
      paymentDate?: string | null;
      transactionId?: string | null;
      matchedMemberId?: string | null;
      isExplicitMatch?: boolean;
    }[]
  >([]);
  const [venmoSlotMappings, setVenmoSlotMappings] = useState<{
    [formMemberIndex: string]: MappedVenmoCandidate | null;
  }>({});
  const [draggedVenmoCandidate, setDraggedVenmoCandidate] =
    useState<MappedVenmoCandidate | null>(null);

  // Source-2 (group-aware AppFolio scrape) candidates for this ARH's listing.
  const [groupOccupantsList, setGroupOccupantsList] = useState<
    MappedAppfolioMember[]
  >([]);

  // dragIds that are in a slot only because of a backend suggestion (matchedMemberId),
  // not because of a saved/confirmed mapping. These should NOT be excluded from
  // Available Leads — otherwise the user can't find them to manually confirm.
  const [suggestedSlotDragIds, setSuggestedSlotDragIds] = useState<Set<string>>(new Set());

  // Same idea for Venmo: slot keys that are pure suggestions (isExplicitMatch=false)
  // should still appear in Available Candidates so the user can manually confirm them.
  const [suggestedVenmoSlotKeys, setSuggestedVenmoSlotKeys] = useState<Set<string>>(new Set());

  const mappingMode: "appfolio" | "venmo" =
    activeTab === "venmo" ? "venmo" : "appfolio";

  // Fetch existing AppFolio mappings for this group
  useEffect(() => {
    let cancelled = false;
    setLoadingMappings(true);
    getTenantMappings(arh.id)
      .then((mappings) => {
        if (!cancelled) setExistingMappings(mappings || []);
      })
      .catch(() => {
        if (!cancelled) setExistingMappings([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingMappings(false);
      });
    return () => {
      cancelled = true;
    };
  }, [arh.id]);

  // Fetch Venmo-extracted names for this group's listing
  useEffect(() => {
    let cancelled = false;
    fetchVenmoCandidates(arh.id)
      .then((candidates) => {
        if (cancelled) return;
        setVenmoCandidatesList(
          (candidates || []).map((c: IVenmoCandidate) => ({
            dragId: c.key,
            displayName: c.name,
            key: c.key,
            amount: c.amount,
            paymentDate: c.paymentDate,
            transactionId: c.transactionId,
            matchedMemberId: c.matchedMemberId,
            isExplicitMatch: c.isExplicitMatch ?? false,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setVenmoCandidatesList([]);
      });
    return () => {
      cancelled = true;
    };
  }, [arh.id]);

  // Fetch Source-2 candidates for this group — mirrors the Venmo candidate fetch above.
  useEffect(() => {
    let cancelled = false;
    fetchGroupOccupantsForArh(arh.id)
      .then((occupants) => {
        if (cancelled) return;
        setGroupOccupantsList(
          (occupants || [])
            .filter((o) => o.name)
            .map((o) => ({
              id: o.id,
              name: o.name,
              email: o.email,
              apartmentName: o.apartmentTitle,
              phoneNo: o.phone || "",
              createdAtUtc: o.createdAtUtc,
              isGuarantor: o.isCosigner,
              role: o.isCosigner ? "guarantor" : "tenant",
              displayName: o.name,
              dragId: o.id,
              source: "groupOccupant",
              matchedMemberId: o.matchedMemberId,
            })),
        );
      })
      .catch(() => {
        if (!cancelled) setGroupOccupantsList([]);
      });
    return () => {
      cancelled = true;
    };
  }, [arh.id]);

  // Same real applicant can be scraped by both AppFolio sources independently.
  // Identity key prefers email (more reliable); falls back to name when email is blank.
  const appfolioIdentityKey = (name?: string, email?: string) => {
    const normalizedEmail = (email || "").trim().toLowerCase();
    if (normalizedEmail) return `email:${normalizedEmail}`;
    return `name:${(name || "").trim().toLowerCase()}`;
  };

  // Member ids belonging to THIS group's own roster — a confirmed assignment
  // pointing to one of these is normal (it's this group's own mapping, shown via
  // existingMappings elsewhere), not a cross-group conflict.
  const ownMemberIds = useMemo(
    () =>
      new Set(
        (memberGuarantorInfo?.memberAndGaurantorInfo || []).map((m) => m.id),
      ),
    [memberGuarantorInfo],
  );

  // Leads/occupants already confirmed to a DIFFERENT group's member — excluded
  // below so the same real applicant can't be dragged onto a second, unrelated
  // member here and saved twice (SaveMappings would also reject it, but it's
  // clearer to never offer it as a candidate in the first place). Tracked by
  // BOTH raw id and identity (name+email): the same real person is often scraped
  // independently into Source 1 and Source 2 as two different rows/ids, so id
  // alone would only hide whichever specific row got confirmed, leaving the
  // other source's copy of the same person looking available.
  const confirmedElsewhereIds = useMemo(() => {
    const ids = new Set<string>();
    (confirmedAppfolioAssignments || []).forEach((a) => {
      if (ownMemberIds.has(a.memberId)) return;
      if (a.appfolioId) ids.add(a.appfolioId);
      if (a.groupOccupantId) ids.add(a.groupOccupantId);
    });
    return ids;
  }, [confirmedAppfolioAssignments, ownMemberIds]);

  const confirmedElsewhereIdentityKeys = useMemo(() => {
    const keys = new Set<string>();
    (confirmedAppfolioAssignments || []).forEach((a) => {
      if (ownMemberIds.has(a.memberId)) return;
      // Only apply the identity-key (email) filter for assignments from the SAME listing.
      // Cross-listing: the same person legitimately has separate applications for different
      // apartments — block by raw ID only, not by identity, so their other-apartment leads
      // remain visible here.
      if (a.listingId === arh.listingID) {
        keys.add(appfolioIdentityKey(a.name, a.email));
      }
    });
    return keys;
  }, [confirmedAppfolioAssignments, ownMemberIds, arh.listingID]);

  // All public tenant leads (Source 1) + group-occupant candidates (Source 2) as one
  // unified draggable list, tagged by source. Source 1 already wins everywhere else
  // (auto-mapping), so a Source-2 entry that's really the same person as an
  // already-listed Source-1 lead is skipped here too — otherwise the same applicant
  // shows up twice with no way to tell they're one person.
  const processedAppfolioData = useMemo(() => {
    const processed: MappedAppfolioMember[] = [];
    const seenIdentities = new Set<string>();

    appfolioData.forEach((member) => {
      if (
        member.name &&
        !confirmedElsewhereIds.has(member.id) &&
        !confirmedElsewhereIdentityKeys.has(
          appfolioIdentityKey(member.name, member.email),
        )
      ) {
        processed.push({
          ...member,
          role: member.isGuarantor ? "guarantor" : "tenant",
          displayName: member.name,
          dragId: member.id,
          source: "publicLead",
        });
        seenIdentities.add(appfolioIdentityKey(member.name, member.email));
      }
    });

    groupOccupantsList.forEach((occupant) => {
      if (confirmedElsewhereIds.has(occupant.id)) return;
      const key = appfolioIdentityKey(occupant.displayName, occupant.email);
      if (confirmedElsewhereIdentityKeys.has(key)) return;
      if (seenIdentities.has(key)) return;
      seenIdentities.add(key);
      processed.push(occupant);
    });

    return processed;
  }, [
    appfolioData,
    groupOccupantsList,
    confirmedElsewhereIds,
    confirmedElsewhereIdentityKeys,
  ]);

  const filteredAppfolioData = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return processedAppfolioData.filter((member) => {
      const nameMatch = member.displayName.toLowerCase().includes(query);
      const emailMatch = (member.email || "").toLowerCase().includes(query);
      const aptMatch = (member.apartmentName || "")
        .toLowerCase()
        .includes(query);
      return nameMatch || emailMatch || aptMatch;
    });
  }, [processedAppfolioData, searchQuery]);

  const assignedLeadIds = useMemo(() => {
    const ids = new Set<string>();
    Object.values(slotMappings).forEach((lead) => {
      // Exclude suggestions — they're not confirmed yet, so they should still
      // appear in Available Leads so the user can manually pick them.
      if (lead && !suggestedSlotDragIds.has(lead.dragId)) ids.add(lead.dragId);
    });
    return ids;
  }, [slotMappings, suggestedSlotDragIds]);

  const availableLeads = useMemo(() => {
    return filteredAppfolioData.filter(
      (member) => !assignedLeadIds.has(member.dragId),
    );
  }, [filteredAppfolioData, assignedLeadIds]);

  const filteredVenmoCandidates = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return venmoCandidatesList.filter((c) =>
      c.displayName.toLowerCase().includes(query),
    );
  }, [venmoCandidatesList, searchQuery]);

  const assignedVenmoKeys = useMemo(() => {
    const keys = new Set<string>();
    Object.values(venmoSlotMappings).forEach((c) => {
      // Skip suggestion-only slots so they stay visible in Available Candidates.
      if (c && !suggestedVenmoSlotKeys.has(c.dragId)) keys.add(c.dragId);
    });
    return keys;
  }, [venmoSlotMappings, suggestedVenmoSlotKeys]);

  const availableVenmoCandidates = useMemo(() => {
    return filteredVenmoCandidates.filter(
      (c) => !assignedVenmoKeys.has(c.dragId),
    );
  }, [filteredVenmoCandidates, assignedVenmoKeys]);

  // Build AppFolio slot mappings from existing DB mappings
  useEffect(() => {
    if (!memberGuarantorInfo?.memberAndGaurantorInfo) {
      setSlotMappings({});
      return;
    }

    const newSlots: { [key: string]: MappedAppfolioMember | null } = {};
    const newSuggestedIds = new Set<string>();

    memberGuarantorInfo.memberAndGaurantorInfo.forEach((formMember, index) => {
      const existingMapping = existingMappings.find(
        (m) => m.id === formMember.id,
      );
      if (existingMapping?.mappedTo) {
        const lead = processedAppfolioData.find(
          (l) => l.id === existingMapping.mappedTo!.id,
        );
        if (lead) {
          newSlots[`${index}`] = lead;
        } else {
          const fallbackLead: MappedAppfolioMember = {
            ...existingMapping.mappedTo,
            role: existingMapping.mappedTo.isGuarantor
              ? "guarantor"
              : "tenant",
            displayName: existingMapping.mappedTo.name,
            dragId: existingMapping.mappedTo.id,
            source: "publicLead",
          };
          newSlots[`${index}`] = fallbackLead;
        }
      } else if (existingMapping?.mappedToGroupOccupant) {
        const occupant = existingMapping.mappedToGroupOccupant;
        const lead = processedAppfolioData.find(
          (l) => l.id === occupant.id && l.source === "groupOccupant",
        );
        if (lead) {
          newSlots[`${index}`] = lead;
        } else {
          newSlots[`${index}`] = {
            id: occupant.id,
            name: occupant.name,
            email: occupant.email,
            apartmentName: occupant.apartmentTitle,
            phoneNo: occupant.phone || "",
            createdAtUtc: occupant.createdAtUtc,
            isGuarantor: occupant.isCosigner,
            role: occupant.isCosigner ? "guarantor" : "tenant",
            displayName: occupant.name,
            dragId: occupant.id,
            source: "groupOccupant",
          };
        }
      } else {
        // No saved mapping yet — fall back to the backend's live-computed suggestion
        // (matchedMemberId), same as how Venmo candidates pre-fill an unconfirmed slot.
        // Nothing has been written to the DB for this; it only becomes real on Save.
        const suggested = processedAppfolioData.find(
          (l) => l.source === "groupOccupant" && l.matchedMemberId === formMember.id,
        );
        newSlots[`${index}`] = suggested || null;
        if (suggested) newSuggestedIds.add(suggested.dragId);
      }
    });

    setSlotMappings(newSlots);
    setSuggestedSlotDragIds(newSuggestedIds);
  }, [memberGuarantorInfo, existingMappings, processedAppfolioData]);

  // Build Venmo slot mappings, pre-seeding with the backend's automatic match
  useEffect(() => {
    if (!memberGuarantorInfo?.memberAndGaurantorInfo) {
      setVenmoSlotMappings({});
      return;
    }

    const newSlots: { [key: string]: MappedVenmoCandidate | null } = {};
    const newSuggestedKeys = new Set<string>();

    memberGuarantorInfo.memberAndGaurantorInfo.forEach((formMember, index) => {
      const match = venmoCandidatesList.find(
        (c) => c.matchedMemberId === formMember.id,
      );
      if (match) {
        newSlots[`${index}`] = {
          dragId: match.key,
          displayName: match.displayName,
          key: match.key,
          amount: match.amount,
          paymentDate: match.paymentDate,
          transactionId: match.transactionId,
          isExplicitMatch: match.isExplicitMatch ?? false,
        };
        // isExplicitMatch=false means this is a name-similarity suggestion, not a saved
        // confirmed mapping — keep it visible in Available Candidates.
        if (!match.isExplicitMatch) newSuggestedKeys.add(match.key);
      } else {
        newSlots[`${index}`] = null;
      }
    });

    setVenmoSlotMappings(newSlots);
    setSuggestedVenmoSlotKeys(newSuggestedKeys);
  }, [memberGuarantorInfo, venmoCandidatesList]);

  // Drag handlers - AppFolio leads
  const handleDragStart = (e: React.DragEvent, lead: MappedAppfolioMember) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggedLead(null);
    setDragOverSlot(null);
  };

  // Drag handlers - Venmo candidates
  const handleVenmoDragStart = (
    e: React.DragEvent,
    candidate: MappedVenmoCandidate,
  ) => {
    setDraggedVenmoCandidate(candidate);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleVenmoDragEnd = () => {
    setDraggedVenmoCandidate(null);
    setDragOverSlot(null);
  };

  const handleSlotDragOver = (e: React.DragEvent, slotKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverSlot(slotKey);
  };

  const handleSlotDragLeave = () => {
    setDragOverSlot(null);
  };

  const handleDropOnSlot = (e: React.DragEvent, slotKey: string) => {
    e.preventDefault();
    setDragOverSlot(null);

    if (mappingMode === "venmo") {
      if (!draggedVenmoCandidate) return;
      setVenmoSlotMappings((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((k) => {
          if (updated[k]?.dragId === draggedVenmoCandidate.dragId) {
            updated[k] = null;
          }
        });
        updated[slotKey] = draggedVenmoCandidate;
        return updated;
      });
      // Explicit drag = user confirmed it; no longer just a suggestion.
      setSuggestedVenmoSlotKeys((prev) => {
        const next = new Set(prev);
        next.delete(draggedVenmoCandidate.dragId);
        return next;
      });
      setDraggedVenmoCandidate(null);
      return;
    }

    if (!draggedLead) return;

    setSlotMappings((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((k) => {
        if (updated[k]?.dragId === draggedLead.dragId) {
          updated[k] = null;
        }
      });
      updated[slotKey] = draggedLead;
      return updated;
    });
    // Once the user explicitly drags a lead into a slot it's no longer just a suggestion.
    setSuggestedSlotDragIds((prev) => {
      const next = new Set(prev);
      next.delete(draggedLead.dragId);
      return next;
    });
    setDraggedLead(null);
  };

  const handleRemoveSlotMapping = (slotKey: string) => {
    if (mappingMode === "venmo") {
      const removedVenmo = venmoSlotMappings[slotKey];
      setVenmoSlotMappings((prev) => ({ ...prev, [slotKey]: null }));
      if (removedVenmo) {
        setSuggestedVenmoSlotKeys((prev) => {
          const next = new Set(prev);
          next.delete(removedVenmo.dragId);
          return next;
        });
      }
      return;
    }
    const removedLead = slotMappings[slotKey];
    setSlotMappings((prev) => ({ ...prev, [slotKey]: null }));
    if (removedLead) {
      setSuggestedSlotDragIds((prev) => {
        const next = new Set(prev);
        next.delete(removedLead.dragId);
        return next;
      });
    }
  };

  const handleSaveMappings = async () => {
    setSaveLoading(true);
    setSaveErrorMessage(null);

    try {
      const formMembers = memberGuarantorInfo?.memberAndGaurantorInfo || [];

      if (formMembers.length === 0) {
        setSaveStatus("success");
        setSaveLoading(false);
        setTimeout(() => setSaveStatus(null), 3000);
        return;
      }

      if (mappingMode === "venmo") {
        const mappings = formMembers.map((formMember, index) => ({
          memberId: formMember.id,
          venmoKey: venmoSlotMappings[`${index}`]?.key || null,
        }));

        await saveVenmoMemberMappings(arh.id, mappings);

        // Re-fetch Venmo candidates so matchedMemberId reflects the save.
        // Without this the slot-building effect re-fills cleared slots from
        // the stale local list and the candidate never reappears as available.
        const refreshedCandidates = await fetchVenmoCandidates(arh.id);
        setVenmoCandidatesList(
          (refreshedCandidates || []).map((c: IVenmoCandidate) => ({
            dragId: c.key,
            displayName: c.name,
            key: c.key,
            amount: c.amount,
            paymentDate: c.paymentDate,
            transactionId: c.transactionId,
            matchedMemberId: c.matchedMemberId,
            isExplicitMatch: c.isExplicitMatch ?? false,
          })),
        );

        await onMappingsUpdated?.();

        setSaveStatus("success");
        setSaveLoading(false);
        setTimeout(() => setSaveStatus(null), 3000);
        return;
      }

      const mappingData = formMembers.map((formMember, index) => {
        const matchedLead = slotMappings[`${index}`] || null;
        const isGroupOccupant = matchedLead?.source === "groupOccupant";
        return {
          id: formMember.id,
          arhId: arh.id,
          name: formMember.name,
          email: formMember.email || "",
          isGuarantor: formMember.isGuarantor,
          isDeleted: false,
          appfolioId: matchedLead && !isGroupOccupant ? matchedLead.id : null,
          groupOccupantId: matchedLead && isGroupOccupant ? matchedLead.id : null,
          mappedTo:
            matchedLead && !isGroupOccupant
              ? {
                  id: matchedLead.id,
                  name: matchedLead.name,
                  email: matchedLead.email,
                  apartmentName: matchedLead.apartmentName,
                  phoneNo: matchedLead.phoneNo,
                  createdAtUtc: matchedLead.createdAtUtc,
                  isGuarantor: matchedLead.isGuarantor,
                }
              : null,
          mappedToGroupOccupant:
            matchedLead && isGroupOccupant
              ? {
                  id: matchedLead.id,
                  name: matchedLead.name,
                  email: matchedLead.email,
                  phone: matchedLead.phoneNo,
                  isCosigner: matchedLead.isGuarantor,
                  apartmentTitle: matchedLead.apartmentName,
                  createdAtUtc: matchedLead.createdAtUtc,
                }
              : null,
          isConfirmed: !!matchedLead,
        };
      });

      await saveTenantMappings(mappingData);

      const updatedMappings = await getTenantMappings(arh.id);
      setExistingMappings(updatedMappings || []);

      // Re-fetch groupOccupants so matchedMemberId reflects the save.
      // Without this the slot-building effect's else branch re-fills cleared
      // groupOccupant slots from the stale list, hiding the lead from available.
      const refreshedOccupants = await fetchGroupOccupantsForArh(arh.id);
      setGroupOccupantsList(
        (refreshedOccupants || [])
          .filter((o) => o.name)
          .map((o) => ({
            id: o.id,
            name: o.name,
            email: o.email,
            apartmentName: o.apartmentTitle,
            phoneNo: o.phone || "",
            createdAtUtc: o.createdAtUtc,
            isGuarantor: o.isCosigner,
            role: o.isCosigner ? "guarantor" : "tenant",
            displayName: o.name,
            dragId: o.id,
            source: "groupOccupant",
            matchedMemberId: o.matchedMemberId,
          })),
      );

      await onMappingsUpdated?.();

      setSaveStatus("success");
    } catch (error) {
      console.error("Error saving mappings:", error);
      setSaveStatus("error");
      setSaveErrorMessage(
        error instanceof Error ? error.message : "Error saving mappings",
      );
    } finally {
      setSaveLoading(false);
      setTimeout(() => {
        setSaveStatus(null);
        setSaveErrorMessage(null);
      }, 5000);
    }
  };

  const formMemberSlots: FormMemberSlot[] = useMemo(() => {
    if (!memberGuarantorInfo?.memberAndGaurantorInfo) return [];
    const activeSlotMappings =
      mappingMode === "venmo" ? venmoSlotMappings : slotMappings;
    return memberGuarantorInfo.memberAndGaurantorInfo.map(
      (formMember, index) => ({
        formMember,
        formMemberIndex: index,
        matchedLead: activeSlotMappings[`${index}`] || null,
      }),
    );
  }, [memberGuarantorInfo, slotMappings, venmoSlotMappings, mappingMode]);

  const roleFilteredSlots = formMemberSlots;

  const isMandatoryFlow =
    arh.workflowType === 0 || arh.isMandatory || isMandatory;

  const tabs: { key: DrawerTab; label: string }[] = [
    { key: "progress", label: "Group Progress" },
    { key: "appfolio", label: "Appfolio Mapping" },
    { key: "venmo", label: "Venmo Mapping" },
  ];

  const isEligibleForBidding =
    (mappingInfo?.mappedMembers ?? 0) > 0 &&
    (editState?.payment ?? "not paid") !== "not paid";

  const renderMappingPanel = () => (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 flex-shrink-0 gap-3">
        <p className="text-sm font-medium text-slate-600">
          {roleFilteredSlots.length} member
          {roleFilteredSlots.length === 1 ? "" : "s"} in this section
        </p>
        {formMemberSlots.length > 0 && (
          <button
            onClick={handleSaveMappings}
            disabled={saveLoading}
            className="flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded text-sm hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            <Save className="w-3.5 h-3.5" />
            {saveLoading ? "Saving..." : "Save"}
          </button>
        )}
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left: Form Members (drop targets) */}
        <div className="w-1/2 border-r border-slate-100 flex flex-col overflow-hidden bg-white">
          <div className="px-4 py-2.5 bg-slate-50/80 border-b border-slate-100">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Form Members
            </p>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {roleFilteredSlots.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-center">
                <p className="text-sm text-slate-400">
                  No form members to map
                </p>
              </div>
            ) : (
              roleFilteredSlots.map((slot) => {
                const slotKey = `${slot.formMemberIndex}`;
                const isOver = dragOverSlot === slotKey;
                const isVenmoSuggestedSlot =
                  mappingMode === "venmo" &&
                  !!slot.matchedLead &&
                  !(slot.matchedLead as MappedVenmoCandidate).isExplicitMatch;
                // AppFolio equivalent: the slot is filled by the live matchedMemberId
                // suggestion (see the slot-building effect above), not by a saved
                // TenantDataToAppfolioDataMap row — i.e. nothing's been written to the
                // DB for it yet, same "pending until Save" state as a Venmo suggestion.
                const isAppfolioSuggestedSlot =
                  mappingMode === "appfolio" &&
                  !!slot.matchedLead &&
                  (slot.matchedLead as MappedAppfolioMember).source ===
                    "groupOccupant" &&
                  !existingMappings.some(
                    (m) =>
                      m.id === slot.formMember.id &&
                      m.mappedToGroupOccupant?.id ===
                        (slot.matchedLead as MappedAppfolioMember).id,
                  );
                const isSuggestedSlot =
                  isVenmoSuggestedSlot || isAppfolioSuggestedSlot;
                return (
                  <div
                    key={slotKey}
                    onDragOver={(e) => handleSlotDragOver(e, slotKey)}
                    onDragLeave={handleSlotDragLeave}
                    onDrop={(e) => handleDropOnSlot(e, slotKey)}
                    className={`relative rounded-xl border-2 p-3 transition-all duration-200 ${
                      isOver
                        ? "border-blue-400 bg-blue-50/50 scale-[1.02] shadow-sm"
                        : isSuggestedSlot
                          ? "border-dashed border-amber-300 bg-amber-50/30"
                          : slot.matchedLead
                            ? "border-emerald-200 bg-emerald-50/30"
                            : "border-dashed border-slate-200 bg-slate-50/50 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className={`flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                            slot.formMember.isGuarantor
                              ? "bg-purple-100 text-purple-700"
                              : "bg-indigo-100 text-indigo-700"
                          }`}
                        >
                          {slot.formMember.isGuarantor ? "Guar" : "Ten"}
                        </span>
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-semibold text-slate-700 truncate">
                            {slot.formMember.name}
                          </span>
                          {slot.formMember.email && (
                            <span
                              className="text-[11px] text-slate-400 truncate -mt-0.5"
                              title={slot.formMember.email}
                            >
                              {slot.formMember.email}
                            </span>
                          )}
                        </div>
                      </div>
                      {slot.matchedLead && (
                        <button
                          onClick={() => handleRemoveSlotMapping(slotKey)}
                          className="flex-shrink-0 ml-1 text-slate-300 hover:text-red-500 hover:bg-red-50 p-1 rounded-full transition-colors"
                        >
                          <Close className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    {slot.matchedLead ? (
                      <div
                        className={`bg-white rounded-lg border shadow-sm px-3 py-2 ${
                          isSuggestedSlot
                            ? "border-amber-100"
                            : "border-emerald-100"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-1.5 mb-0.5">
                          <div className="flex items-center gap-1.5 min-w-0">
                            {isSuggestedSlot ? (
                              <WatchLater className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                            ) : (
                              <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                            )}
                            <div className="text-sm text-slate-800 font-semibold truncate">
                              {slot.matchedLead.displayName}
                            </div>
                          </div>
                          <div className="flex-shrink-0 flex items-center gap-1">
                            {mappingMode === "appfolio" &&
                              (slot.matchedLead as MappedAppfolioMember)
                                .source === "groupOccupant" && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider bg-sky-100 text-sky-700">
                                  AppFolio Group
                                </span>
                              )}
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                                isSuggestedSlot
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-emerald-100 text-emerald-700"
                              }`}
                            >
                              {isSuggestedSlot ? "Suggested" : "Confirmed"}
                            </span>
                          </div>
                        </div>
                        {mappingMode === "venmo" ? (
                          <>
                            <div className="text-xs text-slate-500 pl-5 truncate mt-0.5">
                              $
                              {(slot.matchedLead as MappedVenmoCandidate).amount.toFixed(
                                2,
                              )}
                              {(slot.matchedLead as MappedVenmoCandidate)
                                .paymentDate &&
                                ` • ${new Date(
                                  (slot.matchedLead as MappedVenmoCandidate)
                                    .paymentDate as string,
                                ).toLocaleDateString()}`}
                            </div>
                            {isVenmoSuggestedSlot && (
                              <div className="text-[11px] text-amber-600 pl-5 mt-0.5">
                                Click Save to confirm this mapping
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {(slot.matchedLead as MappedAppfolioMember)
                              .email && (
                              <div
                                className="text-[11px] text-slate-400 pl-5 truncate -mt-0.5"
                                title={
                                  (slot.matchedLead as MappedAppfolioMember)
                                    .email
                                }
                              >
                                {
                                  (slot.matchedLead as MappedAppfolioMember)
                                    .email
                                }
                              </div>
                            )}
                            {(slot.matchedLead as MappedAppfolioMember)
                              .apartmentName && (
                              <div className="text-xs text-slate-500 pl-5 truncate mt-0.5">
                                {
                                  (slot.matchedLead as MappedAppfolioMember)
                                    .apartmentName
                                }
                              </div>
                            )}
                            {isAppfolioSuggestedSlot && (
                              <div className="text-[11px] text-amber-600 pl-5 mt-0.5">
                                Click Save to confirm this mapping
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-4 text-slate-400">
                        <DragIndicator className="w-4 h-4 mb-1 opacity-50" />
                        <span className="text-xs font-medium">
                          {isOver ? "Release to drop" : "Drop lead here"}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right: Drag sources */}
        <div className="w-1/2 flex flex-col overflow-hidden bg-slate-50/30">
          <div className="px-4 py-2.5 bg-slate-50/80 border-b border-slate-100 flex items-center gap-2">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex-1">
              {mappingMode === "venmo" ? "Venmo Payments" : "Available Leads"}
            </p>
          </div>
          <div className="px-3 py-3 border-b border-slate-100 bg-white">
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 transition-colors group-focus-within:text-blue-500" />
              <input
                type="text"
                placeholder={
                  mappingMode === "venmo"
                    ? "Search by name..."
                    : "Search by name, email, or apartment..."
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-slate-400"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {mappingMode === "venmo" ? (
              availableVenmoCandidates.length === 0 ? (
                <p className="text-sm text-slate-400 text-center mt-6">
                  {searchQuery
                    ? "No matching names found"
                    : "No Venmo payments found for this listing"}
                </p>
              ) : (
                availableVenmoCandidates.map((candidate) => (
                  <div
                    key={candidate.dragId}
                    draggable
                    onDragStart={(e) => handleVenmoDragStart(e, candidate)}
                    onDragEnd={handleVenmoDragEnd}
                    className="group flex items-center gap-2 p-2.5 rounded-xl border cursor-grab active:cursor-grabbing select-none transition-all duration-200 hover:shadow-sm border-slate-200 bg-white hover:border-slate-300"
                  >
                    <DragIndicator className="w-4 h-4 text-slate-300 group-hover:text-slate-400 flex-shrink-0 transition-colors" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-700 font-semibold truncate">
                        {candidate.displayName}
                      </div>
                      <div className="text-xs text-slate-500 truncate mt-0.5">
                        ${candidate.amount.toFixed(2)}
                        {candidate.paymentDate &&
                          ` • ${new Date(candidate.paymentDate).toLocaleDateString()}`}
                      </div>
                    </div>
                  </div>
                ))
              )
            ) : loadingMappings ? (
              <div className="flex items-center justify-center h-20">
                <Loading />
              </div>
            ) : availableLeads.length === 0 ? (
              <p className="text-sm text-slate-400 text-center mt-6">
                {searchQuery
                  ? "No matching leads found"
                  : "No public leads available"}
              </p>
            ) : (
              availableLeads.map((member) => (
                <div
                  key={member.dragId}
                  draggable
                  onDragStart={(e) => handleDragStart(e, member)}
                  onDragEnd={handleDragEnd}
                  className="group flex items-center gap-2 p-2.5 rounded-xl border cursor-grab active:cursor-grabbing select-none transition-all duration-200 hover:shadow-sm border-slate-200 bg-white hover:border-slate-300"
                >
                  <DragIndicator className="w-4 h-4 text-slate-300 group-hover:text-slate-400 flex-shrink-0 transition-colors" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className="text-sm text-slate-700 font-semibold truncate">
                        {member.displayName}
                      </div>
                      {member.source === "groupOccupant" && (
                        <span className="flex-shrink-0 text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider bg-sky-100 text-sky-700">
                          AppFolio Group
                        </span>
                      )}
                    </div>
                    {member.email && (
                      <div
                        className="text-[11px] text-slate-400 truncate -mt-0.5"
                        title={member.email}
                      >
                        {member.email}
                      </div>
                    )}
                    {member.apartmentName && (
                      <div className="text-xs text-slate-500 truncate mt-0.5">
                        {member.apartmentName}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative flex-shrink-0 w-full md:w-[480px] lg:w-[580px] xl:w-[640px] h-full bg-white border-l border-gray-200 shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg md:text-xl font-semibold text-gray-900 truncate">
              {arh.firstName} {arh.lastName}'s Group
            </h2>
            {isMandatoryFlow && (
              <span className="inline-flex mt-1 px-1.5 py-0.5 text-[9px] font-bold bg-purple-100 text-purple-700 rounded-full uppercase">
                Mandatory Flow
              </span>
            )}
            <p className="text-sm text-gray-500 truncate">{arh.title}</p>
          </div>
          <div className="flex-shrink-0 flex items-center gap-2 ml-3">
            {onOpenInUnsignedList && (
              <button
                onClick={onOpenInUnsignedList}
                className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded px-2 py-1 transition-colors whitespace-nowrap"
              >
                Open in Unsigned List
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1.5 transition-colors"
            >
              <Close />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 flex-shrink-0 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 md:px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.key
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {activeTab === "progress" ? (
            <div className="p-4 md:p-6 space-y-4">
              {memberGuarantorInfo ? (
                <MemberStatusFlow
                  arhid={arh.id}
                  info={memberGuarantorInfo}
                  mappingInfo={mappingInfo}
                  appointmentStatus={arh.appointmentStatus}
                  isMandatory={isMandatoryFlow}
                  getExternalFormStatus={getExternalFormStatus}
                  getAppfolioStatus={getAppfolioStatus}
                />
              ) : (
                <div className="flex items-center justify-center h-32">
                  <Loading />
                </div>
              )}

              {/* Editable group settings, preserved from the previous table view */}
              <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
                <h4 className="font-semibold text-gray-900 text-sm">
                  Group Settings
                </h4>

                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-700">
                      Tenants:
                    </span>
                    <input
                      type="number"
                      min="0"
                      value={editState?.tenants ?? 0}
                      onChange={(e) =>
                        onEditChange("tenants", parseInt(e.target.value) || 0)
                      }
                      className="w-16 h-8 text-sm border border-gray-300 rounded px-2 text-center focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-700">
                      Guarantors:
                    </span>
                    <input
                      type="number"
                      min="0"
                      value={editState?.guarantors ?? 0}
                      onChange={(e) => {
                        const parsed = parseInt(e.target.value, 10);
                        onEditChange(
                          "guarantors",
                          Number.isNaN(parsed) ? 0 : Math.max(0, parsed),
                        );
                      }}
                      className="w-16 h-8 text-sm border border-gray-300 rounded px-2 text-center focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-700">
                      Payment:
                    </span>
                    <select
                      value={editState?.payment || "not paid"}
                      onChange={(e) => onEditChange("payment", e.target.value)}
                      className={`text-sm rounded-md border px-2 py-1.5 min-w-[160px] ${
                        editState?.payment === "fully paid"
                          ? "bg-green-100 text-green-800"
                          : editState?.payment === "partial paid"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      <option value="not paid">Not Paid</option>
                      <option value="fully paid">Fully Paid</option>
                      <option value="partial paid">Partial Paid</option>
                    </select>
                  </div>

                  <span
                    className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      isEligibleForBidding
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {isEligibleForBidding
                      ? "Eligible for Bidding"
                      : "Not Eligible for Bidding"}
                  </span>

                  {getAppfolioStatus().status === "Completed" &&
                    editState?.payment === "fully paid" && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-700">
                          Bid Status:
                        </span>
                        <select
                          value={editState?.bidding || "No Bid"}
                          onChange={(e) =>
                            onEditChange("bidding", e.target.value)
                          }
                          className={`text-sm rounded-md border px-2 py-1.5 ${
                            editState?.bidding === "Bid"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          <option value="No Bid">No Bid</option>
                          <option value="Bid">Bid</option>
                        </select>
                      </div>
                    )}
                </div>

                <div>
                  <button
                    disabled={!editState?.changed}
                    onClick={onSave}
                    className={`px-4 py-1.5 text-sm font-medium rounded-full ${
                      editState?.changed
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-gray-200 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          ) : (
            renderMappingPanel()
          )}
        </div>

        {/* Save status toast */}
        {saveStatus && (
          <div className="absolute top-4 right-4 z-50 max-w-sm">
            <div
              className={`px-4 py-2 rounded shadow-lg text-sm ${
                saveStatus === "success"
                  ? "bg-green-100 text-green-800 border border-green-300"
                  : "bg-red-100 text-red-800 border border-red-300"
              }`}
            >
              {saveStatus === "success"
                ? "Saved successfully!"
                : saveErrorMessage || "Error saving mappings"}
            </div>
          </div>
        )}
    </div>
  );
};

export default GroupDetailsDrawer;
